import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { Box } from '@fundamentals'
import { localizedStrings } from '@core/strings'
import { Button } from '@mui/material'
import * as Icon from '@mui/icons-material'
import { TemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import { ImageWarning } from '@core/entities/ImageWarning'
import { VirtualizedTemplateAnswers } from './VirtualizedTemplateAnswers'

type RenderAnswersProps = {
  questionBuilderAnswers: TemplateFormAnswers
  permitId?: number
  organisationId: number
  projectId?: number
  warnings?: Array<ImageWarning>
  onlyShowWarnings?: boolean
  titlePrefix?: string
}

export const RenderAnswers: React.FC<RenderAnswersProps> = ({
  questionBuilderAnswers = null,
  permitId = null,
  organisationId,
  projectId,
  warnings = [],
  onlyShowWarnings,
  titlePrefix,
}) => {
  const [show, setShow] = useState(true)
  const submittedBy = questionBuilderAnswers?.submittedBy

  const label = `${
    titlePrefix ? `${titlePrefix}: ` : ''
  }${localizedStrings.answeredByLabel(
    submittedBy?.getFullName?.() ?? '',
  )} - ${moment(questionBuilderAnswers?.submittedOn).format('Do MMM HH:mm')}`

  const questionIds = useMemo(
    () =>
      questionBuilderAnswers?.answers?.flatMap((questionGroup) => {
        return questionGroup?.data?.map((question) => question.id)
      }) || [],
    [questionBuilderAnswers],
  )

  const warningsInAnswers = useMemo(() => {
    return (
      warnings?.filter((warning) =>
        questionIds?.includes(warning.questionId),
      ) || []
    )
  }, [questionIds, warnings])

  const plainAnswers = useMemo(() => {
    return (
      questionBuilderAnswers?.answers?.map((questionGroup) =>
        questionGroup.toPlain(),
      ) || []
    )
  }, [questionBuilderAnswers])

  if (
    !questionBuilderAnswers?.answers?.length ||
    (onlyShowWarnings && (!warnings?.length || !warningsInAnswers?.length))
  ) {
    return null
  }

  return (
    <Box flex={1}>
      <Button
        color='inherit'
        variant='text'
        onClick={() => setShow(!show)}
        sx={{ mb: 1, pl: 0 }}
      >
        <Icon.KeyboardArrowDown
          sx={{
            transform: `rotate(${show ? 0 : 180}deg)`,
            transition: '.3s ease',
          }}
        />
        {label}
      </Button>
      {show && (
        <VirtualizedTemplateAnswers
          questions={plainAnswers}
          organisationId={organisationId}
          projectId={projectId}
          permitId={permitId}
          warnings={warnings}
          onlyShowWarnings={onlyShowWarnings}
          submittedBy={submittedBy}
        />
      )}
    </Box>
  )
}
